// Steuerpaket S3: Anteil eines Eigentümers je Objekt (Eigentümer › Steuer). Geklärt wird im Objekt › Finanzen.
import { useCallback, useEffect, useState } from 'react';
import { ArrowCounterClockwise, ArrowSquareOut, Eye, FilePdf, Info, SealCheck } from '@phosphor-icons/react';
import { BestaetigenDialog, ImmoButton, InnenKarte, KartenTitel, ToastPill, useToastPill } from '../immobilien/ui';
import { gibSteuerpaketFrei, ladeSteuerFreigabe, zieheSteuerpaketZurueck, type FreigabeZeile } from '../../lib/buchhaltung/api';
import { baueSteuerpaketStand, type SteuerpaketStand } from '../../lib/buchhaltung/steuerpaketStand';
import { erzeugeSteuerpaketPdf, steuerpaketDateiname } from '../../lib/buchhaltung/steuerpaketPdf';
import { ladeBriefpapier } from '../../lib/dokumente/blankoBrief';
import { berechne35a, dateiname35a, erzeuge35aPdf, lade35aRechnungen } from '../../lib/buchhaltung/bescheinigung35a';
import { ladeWegEinheiten } from '../../lib/buchhaltung/api';
import { eur } from '../../lib/immobilien/format';
import { istWegUns, type Zuordnung } from '../../lib/eigentuemer/rollen';
import { useAufstellung, useSteuerDaten } from '../../lib/buchhaltung/useSteuerDaten';
import { AufstellungKarte, JahrWahl, KeineSteuerberatung } from './Steuerkarten';

const leise = { fontSize: 12, color: 'var(--ink3)' } as const;

export function SteuerpaketEigentuemer({ eigentuemerId, name, zuordnungen, heute, onObjekt }: {
  eigentuemerId: string; name: string; zuordnungen: readonly Zuordnung[]; heute: string; onObjekt: (objektId: string) => void;
}) {
  const vorjahr = Number(heute.slice(0, 4)) - 1;
  const [jahr, setJahr] = useState(vorjahr);
  const relevant = zuordnungen.filter((z) => z.rolle === 'MV' || istWegUns(z.rolle));
  return (
    <>
      <div className="flex flex-wrap items-center justify-between" style={{ gap: 10 }}>
        <JahrWahl jahre={[vorjahr + 1, vorjahr, vorjahr - 1]} wert={jahr} onChange={setJahr} />
        <KeineSteuerberatung />
      </div>
      {relevant.length === 0 && <InnenKarte><span style={leise}>Keine Objekte in Miet- oder WEG-Verwaltung.</span></InnenKarte>}
      {relevant.map((z) => <ObjektAnteil key={`${z.objektId}|${z.rolle}`} z={z} jahr={jahr} eigentuemerId={eigentuemerId} onObjekt={onObjekt} />)}
      {relevant.some((z) => istWegUns(z.rolle)) && <Bescheinigung35aKarte eigentuemerId={eigentuemerId} name={name} wege={relevant.filter((z) => istWegUns(z.rolle))} jahr={jahr} />}
      {relevant.length > 0 && <FreigabeKarte eigentuemerId={eigentuemerId} name={name} zuordnungen={relevant} jahr={jahr} />}
      <InnenKarte>
        <KartenTitel icon={Info}>Ergänzt der Eigentümer bzw. sein Steuerberater</KartenTitel>
        <span style={leise}>AfA, eigene Darlehen außerhalb der Verwaltung, Grundsteuer (falls nicht über die Verwaltung bezahlt), Fahrtkosten und sonstige eigene Ausgaben.
          Bei mehreren Eigentümern eines Objekts: Aufteilung nach Eigentumsanteil.</span>
      </InnenKarte>
    </>
  );
}

function ObjektAnteil({ z, jahr, eigentuemerId, onObjekt }: { z: Zuordnung; jahr: number; eigentuemerId: string; onObjekt: (id: string) => void }) {
  const weg = istWegUns(z.rolle);
  const d = useSteuerDaten(z.objektId, weg);
  const { ergebnis, verteilung } = useAufstellung(d, jahr);
  const zumObjekt = <ImmoButton art="glas" groesse={32} icon={ArrowSquareOut} onClick={() => onObjekt(z.objektId)}>Im Objekt prüfen</ImmoButton>;

  if (d.laedt) return <InnenKarte><KartenTitel>{z.objektName}</KartenTitel><span style={leise}>Lädt …</span></InnenKarte>;
  if (d.fehler) return <InnenKarte><KartenTitel>{z.objektName}</KartenTitel><span style={{ fontSize: 13, color: 'var(--bad)' }}>{d.fehler}</span></InnenKarte>;
  if (!d.stand || !ergebnis) {
    return (
      <InnenKarte>
        <KartenTitel>{z.objektName}</KartenTitel>
        <span style={leise}>Für dieses Objekt ist noch keine Buchhaltung aus Immoware importiert.</span>
        <div>{zumObjekt}</div>
      </InnenKarte>
    );
  }
  const offen = ergebnis.ungeklaert.length;
  const hinweis = (
    <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
      <span style={{ ...leise, flex: 1, color: offen ? 'var(--warn)' : 'var(--ink3)' }}>
        {offen ? `${offen} ungeklärte Buchung${offen === 1 ? '' : 'en'} – bitte im Objekt klären, vorher nicht freigeben.` : 'Alle Buchungen geklärt.'}
        {` Stand Buchhaltung: ${d.stand.wertstellung_bis ? d.stand.wertstellung_bis.split('-').reverse().join('.') : '–'}.`}
      </span>
      {zumObjekt}
    </div>
  );

  if (!weg) {
    return (
      <>
        <AufstellungKarte titel={`${z.objektName} – Anlage V ${jahr}`} rechts={z.ganzesObjekt ? 'ganzes Objekt' : z.einheiten.map((e) => e.bezeichnung).join(', ')} posten={ergebnis.posten} />
        <InnenKarte padding={12}>{hinweis}</InnenKarte>
      </>
    );
  }
  const meine = verteilung?.einheiten.filter((e) => e.eigentuemer_id === eigentuemerId) ?? [];
  if (!meine.length) {
    return (
      <InnenKarte>
        <KartenTitel>{z.objektName}</KartenTitel>
        <span style={leise}>Keine Einheit mit MEA diesem Eigentümer zugeordnet – Anteil an den WEG-Kosten kann nicht berechnet werden.</span>
        <div>{zumObjekt}</div>
      </InnenKarte>
    );
  }
  const posten = meine.flatMap((e) => e.posten.map((p) => ({ ...p, zusatz: meine.length > 1 ? e.bezeichnung : undefined })));
  const mea = meine.reduce((s, e) => s + (d.einheiten.find((x) => x.einheit_id === e.einheit_id)?.mea ?? 0), 0);
  return (
    <>
      <AufstellungKarte titel={`${z.objektName} – Anteil an den WEG-Kosten ${jahr}`} rechts={`${meine.map((e) => e.bezeichnung).join(', ')} · MEA ${mea}`} posten={posten} nurKosten />
      <InnenKarte padding={12}>
        <span style={leise}>Rücklagenzuführungen zählen nicht. Hausgeld gezahlt laut Buchhaltung ist kein Abzugsbetrag – maßgeblich sind die anteiligen Ausgaben der WEG ({eur(meine.reduce((s, e) => s + e.werbungskosten, 0))}).
          Mieteinnahmen aus der Wohnung erscheinen hier nur, wenn die Mietverwaltung über uns läuft.</span>
        {hinweis}
      </InnenKarte>
    </>
  );
}

/** PDF speichern (Briefpapier; ohne Briefpapier weißes Blatt). */
export async function ladeSteuerpaketPdf(stand: SteuerpaketStand): Promise<void> {
  const papier = await ladeBriefpapier().catch(() => null);
  erzeugeSteuerpaketPdf(stand, papier).save(steuerpaketDateiname(stand));
}

/** Freigabe fürs Eigentümerportal: fester Stand je Jahr; erst ohne ungeklärte Buchungen. */
function FreigabeKarte({ eigentuemerId, name, zuordnungen, jahr }: { eigentuemerId: string; name: string; zuordnungen: readonly Zuordnung[]; jahr: number }) {
  const [freigabe, setFreigabe] = useState<FreigabeZeile | null | undefined>(undefined);
  const [laeuft, setLaeuft] = useState(false);
  const [fragen, setFragen] = useState<'frei' | 'zurueck' | null>(null);
  const [toast, zeige] = useToastPill();
  const lade = useCallback(async () => {
    try { setFreigabe(await ladeSteuerFreigabe(eigentuemerId, jahr)); } catch (e) { setFreigabe(null); zeige((e as Error).message, true); }
  }, [eigentuemerId, jahr, zeige]);
  useEffect(() => { setFreigabe(undefined); void lade(); }, [lade]);

  const vorschau = async () => {
    setLaeuft(true);
    try { const v = await baueSteuerpaketStand(eigentuemerId, name, zuordnungen, jahr); await ladeSteuerpaketPdf(v.stand); }
    catch (e) { zeige(`PDF nicht erstellt: ${(e as Error).message}`, true); }
    finally { setLaeuft(false); }
  };
  const freigeben = async () => {
    setLaeuft(true);
    try {
      const v = await baueSteuerpaketStand(eigentuemerId, name, zuordnungen, jahr);
      if (v.ungeklaert.length) { zeige(`Erst klären: ${v.ungeklaert.map((u) => `${u.objekt} (${u.anzahl})`).join(', ')}`, true); return; }
      if (!v.stand.objekte.length && !v.stand.paragraph35a?.length) { zeige('Keine Buchhaltungsdaten und keine § 35a-Aufwendungen für dieses Jahr – nichts freizugeben.', true); return; }
      await gibSteuerpaketFrei(eigentuemerId, v.stand);
      zeige(`Steuerpaket ${jahr} freigegeben`);
      await lade();
    } catch (e) { zeige(`Nicht freigegeben: ${(e as Error).message}`, true); }
    finally { setLaeuft(false); setFragen(null); }
  };
  const zurueck = async () => {
    setLaeuft(true);
    try { await zieheSteuerpaketZurueck(eigentuemerId, jahr); zeige('Freigabe zurückgezogen'); await lade(); }
    catch (e) { zeige(`Nicht zurückgezogen: ${(e as Error).message}`, true); }
    finally { setLaeuft(false); setFragen(null); }
  };
  const am = freigabe ? new Date(freigabe.freigegeben_am).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
  return (
    <InnenKarte>
      <KartenTitel icon={SealCheck} rechts={freigabe ? `freigegeben ${am}` : freigabe === null ? 'nicht freigegeben' : undefined}>Steuerpaket {jahr} fürs Eigentümerportal</KartenTitel>
      <span style={leise}>
        Mit der Freigabe wird der heutige Stand gespeichert – Anlage-V-Aufstellung und, bei WEG, die § 35a-Bescheinigung. Der Eigentümer sieht beides im Portal unter „Finanzen“ als PDF – nur wenn sein Portal eingeschaltet ist.
        Es geht keine E-Mail raus. Freigabe ist erst möglich, wenn keine Buchung mehr ungeklärt ist. Nach einem neuen Import bitte erneut freigeben.
      </span>
      <div className="flex flex-wrap" style={{ gap: 8 }}>
        <ImmoButton art="glas" icon={Eye} disabled={laeuft} onClick={() => void vorschau()}>Vorschau als PDF</ImmoButton>
        {freigabe && <ImmoButton art="glas" icon={FilePdf} disabled={laeuft} onClick={() => void ladeSteuerpaketPdf(freigabe.stand)}>Freigegebenes PDF</ImmoButton>}
        <ImmoButton art="akzent" icon={SealCheck} disabled={laeuft || freigabe === undefined} onClick={() => setFragen('frei')}>{freigabe ? 'Neu freigeben' : 'Freigeben'}</ImmoButton>
        {freigabe && <ImmoButton art="glas" icon={ArrowCounterClockwise} disabled={laeuft} onClick={() => setFragen('zurueck')}>Zurückziehen</ImmoButton>}
      </div>
      {fragen === 'frei' && (
        <BestaetigenDialog titel={`Steuerpaket ${jahr} freigeben?`} gefahr={false} icon={SealCheck} laeuft={laeuft}
          text={`${name} sieht die Aufstellung ${jahr} danach im Eigentümerportal (sofern freigeschaltet).${freigabe ? ' Der bisher freigegebene Stand wird ersetzt.' : ''}`}
          bestaetigenLabel="Freigeben" onBestaetigen={() => void freigeben()} onAbbrechen={() => setFragen(null)} />
      )}
      {fragen === 'zurueck' && (
        <BestaetigenDialog titel="Freigabe zurückziehen?" text={`Das Steuerpaket ${jahr} verschwindet aus dem Eigentümerportal.`} icon={ArrowCounterClockwise}
          laeuft={laeuft} bestaetigenLabel="Zurückziehen" onBestaetigen={() => void zurueck()} onAbbrechen={() => setFragen(null)} />
      )}
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
    </InnenKarte>
  );
}

/** § 35a-Bescheinigung je WEG (selbst nutzende Eigentümer): bestätigte Lohnanteile der im Jahr bezahlten Rechnungen nach MEA. */
function Bescheinigung35aKarte({ eigentuemerId, name, wege, jahr }: { eigentuemerId: string; name: string; wege: readonly Zuordnung[]; jahr: number }) {
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [toast, zeige] = useToastPill();
  const erstelle = async (z: Zuordnung) => {
    setLaeuft(z.objektId);
    try {
      const [rechnungen, einheiten, papier] = await Promise.all([lade35aRechnungen(z.objektId, jahr), ladeWegEinheiten(z.objektId), ladeBriefpapier().catch(() => null)]);
      if (!einheiten.some((e) => e.eigentuemer_id === eigentuemerId && e.mea)) { zeige('Keine Einheit mit MEA zugeordnet', true); return; }
      const b = berechne35a(rechnungen, einheiten, eigentuemerId, jahr, { objekt: z.objektName, eigentuemer: name });
      erzeuge35aPdf(b, papier).save(dateiname35a(b));
      zeige(b.posten.length ? `Bescheinigung erstellt (${b.posten.length} Rechnungen)` : 'Bescheinigung erstellt – keine begünstigten Rechnungen');
    } catch (e) { zeige(`Nicht erstellt: ${(e as Error).message}`, true); }
    finally { setLaeuft(null); }
  };
  return (
    <InnenKarte>
      <KartenTitel icon={FilePdf}>§ 35a-Bescheinigung {jahr}</KartenTitel>
      <span style={leise}>Für selbst nutzende WEG-Eigentümer: bestätigte Lohnanteile der im Jahr bezahlten Rechnungen der WEG, verteilt nach MEA
        (Sondereigentum ganz an die Einheit). Lohnanteile prüft das Büro im Objekt › Finanzen.</span>
      <div className="flex flex-wrap" style={{ gap: 8 }}>
        {wege.map((z) => <ImmoButton key={z.objektId} art="glas" icon={FilePdf} disabled={!!laeuft} onClick={() => void erstelle(z)}>{laeuft === z.objektId ? 'Erstellt …' : z.objektName}</ImmoButton>)}
      </div>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
    </InnenKarte>
  );
}
